import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Field, Formik } from 'formik';
import type { FormikHelpers, FormikProps } from 'formik';
import { useSettings } from 'context/SettingsContext';
import InputPriceRate from '../../../others/InputPriceRate';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import * as apiWarehouses from 'api/apiWarehouses';
import { getCloudflareUploadUrls, linkCloudflareFiles } from 'api/apiCloudflare';
import { useToast } from 'context/ToastContext';
import FilesCloudUpload from '../../../FilesCloudUpload';
import type { LeadCloudFileType, ObjectDocumentAiDocumentType } from '@econtractor/types';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import PaymentAiPanel from '../PaymentAiPanel';
import usePaymentAiInfo from '../model/usePaymentAiInfo';
import {
    buildPaymentDescriptionFromAi,
    normalizePaymentAmountFromAi,
} from '../model/paymentAiUtils';

interface PaymentFormValues {
    paymentAmount: number;
    paymentAmountRate: number;
    description: string;
}

interface Props {
    wId: number;
    activeTab: string;
    setActiveTab: Dispatch<SetStateAction<string>>;
    productId: number;
    fetchPayments: () => void;
    fetchStocks: (force?: boolean) => void;
    recommendAmount: number;
}

const TabAddPayment = ({
    wId,
    activeTab,
    setActiveTab,
    productId,
    fetchPayments,
    fetchStocks,
    recommendAmount,
}: Props) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current || 1) || 1;
    const refInputPaymentAmount = useRef<HTMLInputElement>(null);
    const formikRef = useRef<FormikProps<PaymentFormValues>>(null);
    const [inputMode, setInputMode] = useState<'form' | 'ai'>('form');
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudFile, setCloudFile] = useState<LeadCloudFileType | null>(null);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);
    const skipRecommendSyncRef = useRef(false);

    const resetAttachmentState = () => {
        setPendingCloudFiles([]);
        setCloudFile(null);
        setCloudProgress(false);
    };

    const handleAiParsed = (
        parsed: ObjectDocumentAiDocumentType,
        parsedCloudFile: LeadCloudFileType,
        file: File,
    ) => {
        const paymentAmount = normalizePaymentAmountFromAi(
            parsed.proposalAmount,
            parsed.pricesInUsd,
            rate,
        );
        const nextValues: PaymentFormValues = {
            paymentAmount,
            paymentAmountRate: Number((paymentAmount * rate).toFixed(2)),
            description: buildPaymentDescriptionFromAi(parsed),
        };

        skipRecommendSyncRef.current = true;
        formikRef.current?.setValues(nextValues);
        setCloudFile(parsedCloudFile);
        setPendingCloudFiles([file]);
        setInputMode('form');
    };

    const {
        refInputFile,
        isBusy: aiBusy,
        uploadProgress: aiUploadProgress,
        phase: aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    } = usePaymentAiInfo({
        onParsed: handleAiParsed,
    });

    const uploadCloudFiles = async (paymentId: number): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            paymentId: Number(paymentId || 0),
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(pendingCloudFiles, uploadConfigs, setCloudProgress);

        setCloudProgress(100);
        return uploaded.filter((item) => item.success).map((item) => item.config);
    };

    const handleSubmit = async (values: PaymentFormValues, { resetForm }: FormikHelpers<PaymentFormValues>) => {
        const resPayment = await apiWarehouses.addPayment(wId, productId, values);
        if (!resPayment?.stat || !resPayment?.paymentId) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        const paymentId = Number(resPayment.paymentId || 0);
        if (cloudFile || pendingCloudFiles.length) {
            try {
                setCloudProgress(1);
                let cloudFiles: LeadCloudFileType[] = [];

                if (cloudFile) {
                    cloudFiles = [cloudFile];
                    setCloudProgress(100);
                } else {
                    cloudFiles = await uploadCloudFiles(paymentId);
                }

                if (!cloudFiles.length) {
                    showToast(t('common:messError'), 'warning');
                } else {
                    const linked = await linkCloudflareFiles(cloudFiles, { paymentId });
                    if (!linked) {
                        showToast(t('common:messError'), 'warning');
                    }
                }
            } catch (e) {
                console.error(e);
                showToast(t('common:messError'), 'warning');
            } finally {
                setCloudProgress(false);
            }
        }

        await fetchPayments();
        await fetchStocks(true);
        showToast(t('messPaymentSuccess'), 'success');
        setActiveTab('payments');
        setInputMode('form');
        skipRecommendSyncRef.current = false;
        resetForm({
            values: {
                paymentAmount: recommendAmount,
                paymentAmountRate: Number((recommendAmount * rate).toFixed(2)),
                description: '',
            },
        });
        resetAttachmentState();
    };

    const handlePendingFilesChange = (next: React.SetStateAction<File[]>) => {
        setPendingCloudFiles((prev) => {
            const resolved = typeof next === 'function' ? next(prev) : next;
            if (
                cloudFile &&
                (!resolved.length ||
                    !resolved.some(
                        (file) =>
                            file.name === cloudFile.fileName &&
                            file.size === cloudFile.fileSize,
                    ))
            ) {
                setCloudFile(null);
            }
            return resolved;
        });
    };

    useEffect(() => {
        if (activeTab === 'add-payment' && inputMode === 'form' && refInputPaymentAmount.current) {
            refInputPaymentAmount.current.focus();
            refInputPaymentAmount.current.select();
        }
    }, [activeTab, inputMode]);

    useEffect(() => {
        if (activeTab !== 'add-payment') {
            setInputMode('form');
        }
    }, [activeTab]);

    useEffect(() => {
        if (skipRecommendSyncRef.current) return;
        formikRef.current?.setFieldValue('paymentAmount', recommendAmount);
        formikRef.current?.setFieldValue(
            'paymentAmountRate',
            Number((recommendAmount * rate).toFixed(2)),
        );
    }, [recommendAmount, rate]);

    const formBusy = cloudProgress !== false || aiBusy;

    return (
        <div className="py-3">
            <div className="btn-group w-100 mb-3">
                <Button
                    size="sm"
                    variant={inputMode === 'form' ? 'primary' : 'outline-primary'}
                    onClick={() => setInputMode('form')}
                    disabled={aiBusy}
                >
                    <i className="bi bi-pencil-square me-1"></i>
                    {t('paymentAi.tabForm')}
                </Button>
                <Button
                    size="sm"
                    variant={inputMode === 'ai' ? 'light' : 'outline-secondary'}
                    className={inputMode === 'ai' ? 'off-coming-ai-tab is-active' : 'off-coming-ai-tab'}
                    onClick={() => setInputMode('ai')}
                    disabled={cloudProgress !== false || aiBusy}
                >
                    <i className="bi bi-stars me-1"></i>
                    {t('paymentAi.tabAi')}
                </Button>
            </div>

            {inputMode === 'ai' && (
                <PaymentAiPanel
                    isBusy={aiBusy}
                    handleOpenClick={handleOpenClick}
                    processingLabel={processingLabel}
                    phase={aiPhase}
                    handleCancel={handleCancel}
                    uploadProgress={aiUploadProgress}
                    refInputFile={refInputFile}
                    handleChangeFile={handleChangeFile}
                />
            )}

            <div className={inputMode === 'form' ? '' : 'd-none'}>
                <Formik
                    innerRef={formikRef}
                    initialValues={{
                        paymentAmount: recommendAmount,
                        paymentAmountRate: Number((recommendAmount * rate).toFixed(2)),
                        description: '',
                    }}
                    onSubmit={handleSubmit}
                >
                    {(formik) => (
                        <form onSubmit={formik.handleSubmit}>
                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('labelAmount')}</label>
                                <InputPriceRate
                                    ref={refInputPaymentAmount}
                                    value={formik.values?.paymentAmount || 0}
                                    disabled={formik.isSubmitting || formBusy}
                                    onValueChange={(val) => {
                                        formik.setFieldValue('paymentAmount', val.floatValue);
                                        formik.setFieldValue(
                                            'paymentAmountRate',
                                            val.floatValue * rate,
                                        );
                                    }}
                                />
                            </Form.Group>

                            <FilesCloudUpload
                                disabled={formik.isSubmitting || formBusy}
                                progress={cloudProgress}
                                files={pendingCloudFiles}
                                setFiles={handlePendingFilesChange}
                                label={
                                    cloudFile
                                        ? t('paymentAi.fileFromAi')
                                        : t('common:labelAttachments')
                                }
                                buttonLabel={t('common:buttons.upload')}
                                uploadingLabel={t('common:buttons.uploading')}
                            />

                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('labelPaymentDescription')}</label>
                                <Field
                                    disabled={formik.isSubmitting || formBusy}
                                    name="description"
                                    as="textarea"
                                    rows="4"
                                    className="form-control"
                                />
                            </Form.Group>

                            <div className="text-end">
                                <Button
                                    type="submit"
                                    size="sm"
                                    variant="primary"
                                    disabled={formik.isSubmitting || formBusy}
                                >
                                    <i className="bi bi-plus-lg me-1 fs-14"></i>
                                    {t('btnAdd')}
                                </Button>
                            </div>
                        </form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default TabAddPayment;
